// apps/web/src/pages/coletor/kanban-tablet/tablet-etapas-anteriores.tsx — Fase 19, Plano 07 (D-13, D-14, D-16, D-17).
// Lista "Etapas anteriores": OPs cuja etapa desta célula ainda não está na fila porque há etapa anterior pendente.
// Busca só local (OP, lote ou item); a lista já vem restrita pelo servidor. Sem hover (ADR-005).
import { useState, type ReactElement } from 'react';
import type { KanbanCartaoTablet } from '@pieltec/contracts';
import { descreverEtapasPuladas, filtrarEtapasAnteriores } from '../../../lib/kanban/tablet';
import { AVISO_CABINE_ALOCADA } from './tablet-concluir-sheet';

export interface TabletEtapasAnterioresProps {
  itens: KanbanCartaoTablet[];
  /** osId com ação em andamento (bloqueia o botão do item). */
  emAndamento: string | null;
  /** O item já tem um apontamento salvo no tablet aguardando envio? */
  aguardandoEnvio: (osId: string) => boolean;
  onRegistrar: (cartao: KanbanCartaoTablet) => void;
}

export interface ListaEtapasAnterioresProps extends TabletEtapasAnterioresProps {
  termo: string;
  onTermoChange: (termo: string) => void;
}

/** Parte apresentacional (sem estado): recebe o termo de busca de fora. */
export function ListaEtapasAnteriores({
  itens,
  termo,
  onTermoChange,
  emAndamento,
  aguardandoEnvio,
  onRegistrar,
}: ListaEtapasAnterioresProps): ReactElement {
  const visiveis = filtrarEtapasAnteriores(itens, termo);

  return (
    <section className="space-y-4" aria-label="Etapas anteriores">
      <input
        type="search"
        inputMode="text"
        aria-label="Buscar por OP, lote ou item"
        placeholder="Buscar por OP, lote ou item"
        value={termo}
        onChange={(e) => onTermoChange(e.target.value)}
        className="w-full min-h-[64px] px-4 bg-slate-950 border border-slate-700 rounded-xl text-lg text-white focus:ring-2 focus:ring-emerald-400"
      />

      {itens.length === 0 ? (
        <div className="py-12 text-center text-sm text-slate-400 bg-slate-900/40 rounded-2xl border border-slate-800/80">
          Nenhuma OP com etapas anteriores pendentes para esta célula.
        </div>
      ) : visiveis.length === 0 ? (
        <div className="py-8 text-center text-sm text-slate-400 bg-slate-900/40 rounded-2xl border border-slate-800/80">
          Nenhum item encontrado para &quot;{termo.trim()}&quot;.
        </div>
      ) : (
        visiveis.map((item) => {
          const aguardando = aguardandoEnvio(item.osId);
          const ocupado = emAndamento === item.osId;
          const puladas = item.etapasPuladas ?? [];
          return (
            <article
              key={item.osId}
              data-testid="tablet-etapa-anterior"
              className="rounded-2xl border border-slate-800 bg-slate-900 p-5 space-y-3 shadow-lg"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2 py-0.5 bg-emerald-700/30 border border-emerald-500/40 text-blue-300 text-xs font-mono font-bold rounded">
                  OP-{item.opNumero}
                </span>
                <span className="text-xs font-mono text-slate-400">Lote {item.loteCodigo}</span>
              </div>
              <div>
                <p className="text-lg font-bold text-white">{item.itemNome}</p>
                <p className="text-sm text-slate-300">
                  {item.clienteNome}
                  {item.corCodigo ? ` · Cor ${item.corCodigo}` : ''}
                </p>
              </div>
              <p className="text-base font-mono text-slate-100">
                pendente {item.quantidadePendente} de {item.quantidadeAlvo}
              </p>
              {puladas.length > 0 && (
                <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                  Lança registro tardio em: {descreverEtapasPuladas(puladas)}
                </p>
              )}
              {item.naoAlocado && <p className="text-sm text-sky-200">{AVISO_CABINE_ALOCADA}</p>}
              <div className="flex flex-wrap gap-3 pt-1">
                <button
                  type="button"
                  disabled={ocupado || aguardando}
                  onClick={() => onRegistrar(item)}
                  className="min-h-[64px] min-w-[140px] px-6 font-bold text-base rounded-xl touch-manipulation text-white disabled:opacity-50 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700"
                >
                  {aguardando ? 'Aguardando envio' : 'Registrar e avançar'}
                </button>
              </div>
            </article>
          );
        })
      )}
    </section>
  );
}

export function TabletEtapasAnteriores(props: TabletEtapasAnterioresProps): ReactElement {
  const [termo, setTermo] = useState<string>('');
  return <ListaEtapasAnteriores {...props} termo={termo} onTermoChange={setTermo} />;
}
